import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  ReactFlow,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { AuthProvider, useAuth } from './auth/AuthContext';
import LoginPage from './auth/LoginPage';
import RegisterPage from './auth/RegisterPage';
import AssetNode from './nodes/AssetNode';
import CenecSidebar from './components/CenecSidebar';
import Toolbar from './components/Toolbar';
import PropertiesPanel from './components/PropertiesPanel';
import UserManagement from './components/UserManagement';
import ProjectsView from './components/ProjectsView';
import AssetDefinitionsView from './components/AssetDefinitionsView';
import NodeDefinitionsView from './components/NodeDefinitionsView';
import LineDefinitionsView from './components/LineDefinitionsView';
import PVSystemsView from './components/PVSystemsView';
import ProcessMediaView from './components/ProcessMediaView';
import SystemModulesView from './components/SystemModulesView';
import ClimateDataView from './components/ClimateDataView';
import FrequencyDataView from './components/FrequencyDataView';
import CommoditiesView from './components/CommoditiesView';
import GridMarketView from './components/GridMarketView';
import CanvasViewer from './components/CanvasViewer';
import './App.css';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;
const nodeTypes = { asset: AssetNode };

const authHeaders = () => {
  const token = localStorage.getItem('nrg-token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = 'Bearer ' + token;
  return headers;
};

const defaultEdgeOptions = {
  type: 'smoothstep',
  animated: true,
  style: { stroke: '#4ecca3', strokeWidth: 2 },
  markerEnd: { type: MarkerType.ArrowClosed, color: '#4ecca3' },
};

let nodeId = 1;

function Canvas() {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showUsers, setShowUsers] = useState(false);
  const [activeView, setActiveView] = useState('projects');
  const [canvasContext, setCanvasContext] = useState(null);
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const layoutIdRef = useRef(null);
  const canvasLoaded = useRef(false);

  // Load canvas from API on mount
  useEffect(() => {
    if (canvasLoaded.current) return;
    canvasLoaded.current = true;

    (async () => {
      try {
        const res = await fetch(`${API}/api/canvas-layouts`, { headers: authHeaders() });
        if (res.ok) {
          const data = await res.json();
          const layouts = Array.isArray(data) ? data : (data.layouts || []);
          if (layouts.length > 0) {
            const layout = layouts[0];
            layoutIdRef.current = layout.id;
            if (layout.nodes?.length) {
              setNodes(layout.nodes);
              const maxId = Math.max(...layout.nodes.map((n) => parseInt(n.id) || 0));
              nodeId = maxId + 1;
            }
            if (layout.edges?.length) {
              setEdges(layout.edges);
            }
            return;
          }
        }
      } catch (err) {
        console.warn('Canvas API unavailable, starting with empty canvas:', err.message);
      }
    })();
  }, [setNodes, setEdges]);

  // Auto-save to API (debounced 500ms)
  useEffect(() => {
    // Don't save until initial load is done
    if (!canvasLoaded.current) return;

    const timer = setTimeout(async () => {
      try {
        if (layoutIdRef.current) {
          // Update existing layout
          await fetch(`${API}/api/canvas-layouts/${layoutIdRef.current}`, {
            method: 'PUT',
            headers: authHeaders(),
            body: JSON.stringify({ nodes, edges }),
          });
        } else if (nodes.length > 0 || edges.length > 0) {
          // Create new layout
          const res = await fetch(`${API}/api/canvas-layouts`, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({ name: 'Default Layout', nodes, edges }),
          });
          if (res.ok) {
            const data = await res.json();
            layoutIdRef.current = data.id || data.layout?.id;
          }
        }
      } catch (err) {
        // Silently fail auto-save — user can still export manually
        console.warn('Auto-save failed:', err.message);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [nodes, edges]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ ...params, ...defaultEdgeOptions }, eds)),
    [setEdges]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const data = event.dataTransfer.getData('application/reactflow');
      if (!data) return;
      const def = JSON.parse(data);
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      // Build per-instance properties from the definition's declared fields,
      // using each field's declared defaultValue. `status` is hoisted to the
      // top of node.data so we skip it here to avoid a duplicate key.
      const properties = {};
      let initialStatus = 'active';
      (def.fields || []).forEach((f) => {
        if (f.name === 'status') {
          if (f.defaultValue) initialStatus = f.defaultValue;
          return;
        }
        properties[f.name] = f.defaultValue !== undefined ? f.defaultValue : '';
      });
      const newNode = {
        id: String(nodeId++),
        type: 'asset',
        position,
        data: {
          label: def.name,
          icon: def.icon,
          color: def.frameColor,
          assetType: def.id,
          status: initialStatus,
          properties,
        },
      };
      setNodes((nds) => [...nds, newNode]);
    },
    [reactFlowInstance, setNodes]
  );

  const onNodeClick = useCallback((_, node) => setSelectedNode(node), []);
  const onPaneClick = useCallback(() => setSelectedNode(null), []);

  const handleUpdateNode = useCallback(
    (id, updates) => {
      setNodes((nds) => nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...updates } } : n)));
      setSelectedNode(null);
    },
    [setNodes]
  );

  const handleSave = () => {
    const data = JSON.stringify({ nodes, edges }, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nrg-canvas-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleLoad = (data) => {
    if (data.nodes) setNodes(data.nodes);
    if (data.edges) setEdges(data.edges);
    const maxId = Math.max(...(data.nodes || []).map((n) => parseInt(n.id) || 0), 0);
    nodeId = maxId + 1;
  };

  const handleClear = () => {
    if (window.confirm('Clear entire canvas?')) {
      setNodes([]);
      setEdges([]);
      nodeId = 1;
    }
  };

  const handleExport = () => {
    const exportData = {
      version: '1.0',
      exportedAt: new Date().toISOString(),
      assets: nodes.map((n) => ({
        id: n.id, type: n.data.assetType, label: n.data.label,
        status: n.data.status, properties: n.data.properties, position: n.position,
      })),
      connections: edges.map((e) => ({
        id: e.id, from: e.source, to: e.target,
        fromPort: e.sourceHandle, toPort: e.targetHandle,
      })),
    };
    const data = JSON.stringify(exportData, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nrg-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="app">
      {activeView !== 'canvas-viewer' && (
        <CenecSidebar
          activeView={activeView}
          onViewChange={setActiveView}
          onShowUsers={() => setShowUsers(true)}
        />
      )}
      {activeView === 'canvas-viewer' && canvasContext && (
        <CanvasViewer
          context={canvasContext}
          onBack={() => { setActiveView('projects'); setCanvasContext(null); }}
        />
      )}
      {activeView === 'projects' && (
        <div className="main-content">
          <ProjectsView onOpenCanvas={(ctx) => { setCanvasContext(ctx); setActiveView('canvas-viewer'); }} />
        </div>
      )}
      {activeView === 'definitions' && (
        <div className="main-content">
          <AssetDefinitionsView />
        </div>
      )}
      {activeView === 'nodes' && (
        <div className="main-content">
          <NodeDefinitionsView />
        </div>
      )}
      {activeView === 'lines' && (
        <div className="main-content">
          <LineDefinitionsView />
        </div>
      )}
      {activeView === 'media' && (
        <div className="main-content">
          <ProcessMediaView />
        </div>
      )}
      {activeView === 'pvsystems' && (
        <div className="main-content">
          <PVSystemsView />
        </div>
      )}
      {activeView === 'system' && (
        <div className="main-content">
          <SystemModulesView />
        </div>
      )}
      {activeView === 'climate' && (
        <div className="main-content">
          <ClimateDataView />
        </div>
      )}
      {activeView === 'frequency' && (
        <div className="main-content">
          <FrequencyDataView />
        </div>
      )}
      {activeView === 'commodities' && (
        <div className="main-content">
          <CommoditiesView />
        </div>
      )}
      {activeView === 'gridmarket' && (
        <div className="main-content">
          <GridMarketView />
        </div>
      )}
      {activeView === 'canvas' && (
        <>
          <div className="canvas-container">
            <Toolbar onSave={handleSave} onLoad={handleLoad} onClear={handleClear} onExport={handleExport} />
            <div className="canvas-wrapper" ref={reactFlowWrapper}>
              <ReactFlow
                nodes={nodes} edges={edges}
                onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
                onConnect={onConnect} onInit={setReactFlowInstance}
                onDrop={onDrop} onDragOver={onDragOver}
                onNodeClick={onNodeClick} onPaneClick={onPaneClick}
                nodeTypes={nodeTypes} defaultEdgeOptions={defaultEdgeOptions}
                fitView snapToGrid snapGrid={[16, 16]}
                deleteKeyCode="Backspace" multiSelectionKeyCode="Shift"
              >
                <Controls />
                <MiniMap
                  nodeColor={(n) => n.data?.color || '#4ecca3'}
                  maskColor="rgba(26, 26, 46, 0.7)"
                  style={{ background: '#1e2a4a' }}
                />
                <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#1e2a4a" />
              </ReactFlow>
            </div>
          </div>
          {selectedNode && (
            <PropertiesPanel node={selectedNode} onUpdate={handleUpdateNode} onClose={() => setSelectedNode(null)} />
          )}
        </>
      )}
      {showUsers && <UserManagement onClose={() => { setShowUsers(false); setActiveView('definitions'); }} />}
    </div>
  );
}

function AuthGate() {
  const { user, loading } = useAuth();
  const [showRegister, setShowRegister] = useState(false);

  if (loading) {
    return (
      <div className="auth-page">
        <div className="auth-loading">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return showRegister
      ? <RegisterPage onToggle={() => setShowRegister(false)} />
      : <LoginPage onToggle={() => setShowRegister(true)} />;
  }

  return <Canvas />;
}

function App() {
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}

export default App;
